import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '../../components/ui/Button';
import SearchableSelectBox from '../../components/ui/SearchableSelectBox';
import { useToast } from '../../hooks/useToast';
import FastAPIClient from '../../services/apiClient';
import {
    fetchInventoryForStore,
    fetchUtilCustomers,
    // linkDiscountRuleToStore,
    // unlinkDiscountRuleFromStore,
    // linkDiscountRuleToCustomer,
    // unlinkDiscountRuleFromCustomer,
    // linkDiscountRuleToProduct,
    // unlinkDiscountRuleFromProduct,
    // linkDiscountRuleToCategory,
    // unlinkDiscountRuleFromCategory,
    fetchDiscountRules // To get the latest linked data for the specific rule
} from '../../apiHelper';
import { Plus, Trash2, Loader2, Store, Users, Package, Tag } from 'lucide-react';

const client = new FastAPIClient();

/**
 * DiscountLinkageManager Component
 * Manages the linking and unlinking of stores, customers, products, and categories
 * to a specific discount rule. This component is designed to be used as a standalone
 * view or within a dedicated section, not as a modal.
 *
 * @param {object} props - Component props.
 * @param {object} props.user - The authenticated user object from local storage.
 * @param {number} props.discountRuleId - The ID of the discount rule to manage linkages for.
 * @param {Array} props.stores - List of all available stores.
 * @param {Array} props.categories - List of all available categories.
 * @param {function} props.onClose - Callback function to signal that the linkage management is complete.
 */
const DiscountLinkageManager = ({ user, discountRuleId, stores, categories, onClose }) => {
    const { showToast } = useToast();

    const [currentRuleData, setCurrentRuleData] = useState(null); // Full data of the discount rule being linked
    const [loadingRuleData, setLoadingRuleData] = useState(true);
    const [errorRuleData, setErrorRuleData] = useState(null);

    // States for adding new linkages
    const [selectedStoreToLink, setSelectedStoreToLink] = useState(null);
    const [selectedCustomerToLink, setSelectedCustomerToLink] = useState(null);
    const [selectedProductToLink, setSelectedProductToLink] = useState(null);
    const [selectedCategoryToLink, setSelectedCategoryToLink] = useState(null);

    // Data for searchable select boxes
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [inventory, setInventory] = useState([]); // Products for selected store

    const [linkingStatus, setLinkingStatus] = useState({
        store: false, customer: false, product: false, category: false
    });

    // Fetch the specific discount rule's full data to get its current linkages
    const loadCurrentRuleData = useCallback(async () => {
        if (!user || !discountRuleId) return;
        setLoadingRuleData(true);
        setErrorRuleData(null);
        // console.log(user)
        try {
            // Fetch all rules and find the one matching discountRuleId
            const payload = {
                users_id: user.users_id,
                logon_id: user.logon_id,
                hpwc: user.hpwc,
                offset: 0,
                limit: 1000 // Fetch a reasonable number to find the rule
            };
            const allRules = await fetchDiscountRules(payload);
            const rule = allRules.find(r => r.discountrule_id === discountRuleId);
            // console.log(rule)

            if (rule) {
                setCurrentRuleData(rule);
            } else {
                setErrorRuleData("Discount rule not found.");
                showToast("Discount rule details could not be loaded.", 'error');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load discount rule details: ${err.message || err}`;
            setErrorRuleData(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingRuleData(false);
        }
    }, [user, discountRuleId, showToast]);


    // Fetch customers for the dropdown
    const loadCustomers = useCallback(async () => {
        if (!user) return;
        setLoadingCustomers(true);
        try {
            const fetchedCustomers = await fetchUtilCustomers(user, ''); // Fetch all customers initially
            setCustomerOptions(fetchedCustomers.map(customer => ({
                label: `${customer.user.first_name} ${customer.user.last_name} (${customer.user.credential.logon_id})`,
                value: customer.member_id,
            })));
        } catch (err) {
            showToast(`Failed to load customers: ${err.message || err}`, 'error');
            console.error("Error loading customers:", err);
            setCustomerOptions([]);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, showToast]);

    // Fetch inventory for a specific store (for product linking)
    const loadInventoryForStore = useCallback(async (storeId) => {
        if (!user || !storeId) {
            setInventory([]);
            return;
        }
        setLoadingInventory(true);
        try {
            const payload = {
                users_id: user.users_id,
                logon_id: user.logon_id,
                hpwc: user.hpwc,
                store_id: storeId,
                offset: 0,
                limit: 1000
            };
            const fetchedInventoryLots = await fetchInventoryForStore(payload);
            const aggregatedProductsMap = new Map();
            fetchedInventoryLots.forEach(lot => {
                const catentryId = lot.catentry.catentry_id;
                if (!aggregatedProductsMap.has(catentryId)) {
                    aggregatedProductsMap.set(catentryId, {
                        id: catentryId,
                        name: lot.catentry.productname,
                        partnumber: lot.catentry.partnumber,
                    });
                }
            });
            setInventory(Array.from(aggregatedProductsMap.values()));
        } catch (err) {
            showToast(`Failed to load inventory for selected store: ${err.message || err}`, 'error');
            console.error("Error loading inventory:", err);
            setInventory([]);
        } finally {
            setLoadingInventory(false);
        }
    }, [user, showToast]);


    useEffect(() => {
        loadCurrentRuleData();
        loadCustomers();
    }, [loadCurrentRuleData, loadCustomers]);

    // Derived options for select boxes
    const storeOptions = useMemo(() => stores.map(store => ({
        value: store.store_id,
        label: store.identifier
    })), [stores]);

    const customerOptionsMemo = useMemo(() => customerOptions, [customerOptions]);

    const productOptions = useMemo(() => inventory.map(product => ({
        value: product.id,
        label: `${product.name} (${product.partnumber})`
    })), [inventory]);

    const categoryOptions = useMemo(() => categories.map(category => ({
        value: category.category_id,
        label: category.identifier
    })), [categories]);


    // Handlers for linking entities
    const handleLinkStore = async () => {
        if (!selectedStoreToLink) {
            showToast("Please select a store to link.", 'info');
            return;
        }
        setLinkingStatus(prev => ({ ...prev, store: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                store_id: selectedStoreToLink,
                creator_id: user.users_id,
            };
            const response = await client.postData('/accounting/link-store-to-discountrule',payload)
            // await linkDiscountRuleToStore(payload); // Using new API helper function
            showToast("Store linked successfully!", 'success');
            setSelectedStoreToLink(null); // Clear selection
            loadCurrentRuleData(); // Refresh linked data
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to link store: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, store: false }));
        }
    };

    const handleUnlinkStore = async (storeId) => {
        if (!window.confirm('Are you sure you want to unlink this store?')) return;
        setLinkingStatus(prev => ({ ...prev, store: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                store_id: storeId,
                creator_id: user.users_id,
            };
            // await unlinkDiscountRuleFromStore(payload); // Using new API helper function
            // showToast("Store unlinked successfully!", 'success');
            // loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to unlink store: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, store: false }));
        }
    };

    const handleLinkCustomer = async () => {
        if (!selectedCustomerToLink) {
            showToast("Please select a customer to link.", 'info');
            return;
        }
        setLinkingStatus(prev => ({ ...prev, customer: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                customer_id: selectedCustomerToLink,
                creator_id: user.users_id,
            };
            // console.log(payload)
            // await linkDiscountRuleToCustomer(payload); // Using new API helper function
            const response = await client.postData('/accounting/link-customer-to-discount',payload)
            showToast("Customer linked successfully!", 'success');
            setSelectedCustomerToLink(null);
            loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to link customer: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, customer: false }));
        }
    };

    const handleUnlinkCustomer = async (customerId) => {
        if (!window.confirm('Are you sure you want to unlink this customer?')) return;
        setLinkingStatus(prev => ({ ...prev, customer: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                customer_id: customerId,
                creator_id: user.users_id,
            };
            // await unlinkDiscountRuleFromCustomer(payload); // Using new API helper function
            // showToast("Customer unlinked successfully!", 'success');
            // loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to unlink customer: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, customer: false }));
        }
    };

    const handleLinkProduct = async () => {
        if (!selectedProductToLink) {
            showToast("Please select a product to link.", 'info');
            return;
        }
        setLinkingStatus(prev => ({ ...prev, product: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                product_id: selectedProductToLink, // This matches catentry_id
                creator_id: user.users_id,
            };
            console.log(payload)
            const response = await client.postData('/accounting/link-product-to-discount',payload)
            // await linkDiscountRuleToProduct(payload); // Using new API helper function
            showToast("Product linked successfully!", 'success');
            setSelectedProductToLink(null);
            loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to link product: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, product: false }));
        }
    };

    const handleUnlinkProduct = async (productId) => {
        if (!window.confirm('Are you sure you want to unlink this product?')) return;
        setLinkingStatus(prev => ({ ...prev, product: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                product_id: productId,
            };
            const response = await client.postData('/accounting/unlink-catentry-from-discount', payload);
            showToast(response.msg || 'Successfully removed product-discount attachment', 'success');
            // await unlinkDiscountRuleFromProduct(payload); // Using new API helper function
            // showToast("Product unlinked successfully!", 'success');
            loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to unlink product: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, product: false }));
        }
    };

    const handleLinkCategory = async () => {
        if (!selectedCategoryToLink) {
            showToast("Please select a category to link.", 'info');
            return;
        }
        setLinkingStatus(prev => ({ ...prev, category: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                category_id: selectedCategoryToLink,
                creator_id: user.users_id,
            };
            const response = await client.postData('/accounting/link-category-to-discount',payload)
            // await linkDiscountRuleToCategory(payload); // Using new API helper function
            showToast("Category linked successfully!", 'success');
            setSelectedCategoryToLink(null);
            loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to link category: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, category: false }));
        }
    };

    const handleUnlinkCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to unlink this category?')) return;
        setLinkingStatus(prev => ({ ...prev, category: true }));
        try {
            const payload = {
                discountrule_id: discountRuleId,
                category_id: categoryId,
                creator_id: user.users_id,
            };
            // await unlinkDiscountRuleFromCategory(payload); // Using new API helper function
            // showToast("Category unlinked successfully!", 'success');
            // loadCurrentRuleData();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to unlink category: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setLinkingStatus(prev => ({ ...prev, category: false }));
        }
    };


    if (loadingRuleData) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin h-8 w-8 text-gmail-blue mr-3" />
                <p className="text-lg text-text-secondary">Loading discount rule details...</p>
            </div>
        );
    }

    if (errorRuleData) {
        return (
            <div className="text-center p-8 text-red-600">
                <p>Error: {errorRuleData}</p>
                <Button onClick={loadCurrentRuleData} className="mt-4">Retry</Button>
            </div>
        );
    }

    if (!currentRuleData) {
        return <div className="text-center p-8 text-text-secondary">No discount rule selected for linkage management.</div>;
    }

    return (
        <div className="p-6 space-y-8 bg-white rounded-lg shadow-md h-full overflow-y-auto custom-scrollbar">
            <h3 className="text-2xl font-bold text-text-primary mb-6">
                Manage Linkages for Rule: <span className="text-gmail-blue">"{currentRuleData.name}"</span> (ID: {currentRuleData.discountrule_id})
            </h3>

            {/* Link Stores */}
            <div className="border p-4 rounded-lg shadow-sm space-y-4">
                <h4 className="text-lg font-semibold flex items-center gap-2"><Store size={20} /> Link to Stores</h4>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <SearchableSelectBox
                            label="Select Store"
                            id="link-store"
                            name="link-store"
                            value={selectedStoreToLink}
                            onChange={(e) => setSelectedStoreToLink(parseInt(e.target.value, 10))}
                            options={storeOptions}
                            placeholder="Select a store to link"
                            required={false}
                        />
                    </div>
                    <Button onClick={handleLinkStore} disabled={!selectedStoreToLink || linkingStatus.store}>
                        {linkingStatus.store ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus size={20} />}
                    </Button>
                </div>
                <div className="mt-4">
                    <p className="font-medium text-text-secondary">Currently Linked Stores:</p>
                    {currentRuleData.stores && currentRuleData.stores.length > 0 ? (
                        <ul className="list-disc list-inside text-sm text-text-primary">
                            {currentRuleData.stores.map(store => (
                                <li key={store.store_id} className="flex items-center justify-between py-1">
                                    {store.identifier}
                                    <Button variant="ghost" size="sm" onClick={() => handleUnlinkStore(store.store_id)} disabled={linkingStatus.store}>
                                        <Trash2 size={16} className="text-red-500" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">No stores linked.</p>
                    )}
                </div>
            </div>

            {/* Link Customers */}
            <div className="border p-4 rounded-lg shadow-sm space-y-4">
                <h4 className="text-lg font-semibold flex items-center gap-2"><Users size={20} /> Link to Customers</h4>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <SearchableSelectBox
                            label="Select Customer"
                            id="link-customer"
                            name="link-customer"
                            value={selectedCustomerToLink}
                            onChange={(e) => setSelectedCustomerToLink(parseInt(e.target.value, 10))}
                            options={customerOptionsMemo}
                            placeholder={loadingCustomers ? "Loading customers..." : "Select a customer to link"}
                            loading={loadingCustomers}
                            disabled={loadingCustomers}
                            required={false}
                        />
                    </div>
                    <Button onClick={handleLinkCustomer} disabled={!selectedCustomerToLink || linkingStatus.customer}>
                        {linkingStatus.customer ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus size={20} />}
                    </Button>
                </div>
                <div className="mt-4">
                    <p className="font-medium text-text-secondary">Currently Linked Customers:</p>
                    {currentRuleData.customers && currentRuleData.customers.length > 0 ? (
                        <ul className="list-disc list-inside text-sm text-text-primary">
                            {currentRuleData.customers.map(customer => (
                                <li key={customer.member_id} className="flex items-center justify-between py-1">
                                    {customer.user.first_name} {customer.user.last_name} ({customer.user.credential.logon_id})
                                    <Button variant="ghost" size="sm" onClick={() => handleUnlinkCustomer(customer.member_id)} disabled={linkingStatus.customer}>
                                        <Trash2 size={16} className="text-red-500" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">No customers linked.</p>
                    )}
                </div>
            </div>

            {/* Link Products */}
            <div className="border p-4 rounded-lg shadow-sm space-y-4">
                <h4 className="text-lg font-semibold flex items-center gap-2"><Package size={20} /> Link to Products</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SearchableSelectBox
                        label="Select Store (for Product Inventory)"
                        id="select-store-for-product"
                        name="select-store-for-product"
                        value={selectedStoreToLink || ''} // Use selectedStoreToLink to load inventory
                        onChange={(e) => {
                            const newStoreId = parseInt(e.target.value, 10);
                            setSelectedStoreToLink(newStoreId);
                            loadInventoryForStore(newStoreId);
                            setSelectedProductToLink(null); // Clear product selection when store changes
                        }}
                        options={storeOptions}
                        placeholder="Select a store to load products"
                        required={false}
                    />
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <SearchableSelectBox
                                label="Select Product"
                                id="link-product"
                                name="link-product"
                                value={selectedProductToLink}
                                onChange={(e) => setSelectedProductToLink(parseInt(e.target.value, 10))}
                                options={productOptions}
                                placeholder={loadingInventory ? "Loading products..." : "Select a product to link"}
                                loading={loadingInventory}
                                disabled={loadingInventory || !selectedStoreToLink}
                                required={false}
                            />
                        </div>
                        <Button onClick={handleLinkProduct} disabled={!selectedProductToLink || linkingStatus.product}>
                            {linkingStatus.product ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus size={20} />}
                        </Button>
                    </div>
                </div>
                <div className="mt-4">
                    <p className="font-medium text-text-secondary">Currently Linked Products:</p>
                    {currentRuleData.products && currentRuleData.products.length > 0 ? (
                        <ul className="list-disc list-inside text-sm text-text-primary">
                            {currentRuleData.products.map(product => (
                                <li key={product.catentry_id} className="flex items-center justify-between py-1">
                                    {product.productname} ({product.partnumber})
                                    <Button variant="ghost" size="sm" onClick={() => handleUnlinkProduct(product.catentry_id)} disabled={linkingStatus.product}>
                                        <Trash2 size={16} className="text-red-500" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">No products linked.</p>
                    )}
                </div>
            </div>

            {/* Link Categories */}
            <div className="border p-4 rounded-lg shadow-sm space-y-4">
                <h4 className="text-lg font-semibold flex items-center gap-2"><Tag size={20} /> Link to Categories</h4>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <SearchableSelectBox
                            label="Select Category"
                            id="link-category"
                            name="link-category"
                            value={selectedCategoryToLink}
                            onChange={(e) => setSelectedCategoryToLink(parseInt(e.target.value, 10))}
                            options={categoryOptions}
                            placeholder="Select a category to link"
                            required={false}
                        />
                    </div>
                    <Button onClick={handleLinkCategory} disabled={!selectedCategoryToLink || linkingStatus.category}>
                        {linkingStatus.category ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus size={20} />}
                    </Button>
                </div>
                <div className="mt-4">
                    <p className="font-medium text-text-secondary">Currently Linked Categories:</p>
                    {currentRuleData.categories && currentRuleData.categories.length > 0 ? (
                        <ul className="list-disc list-inside text-sm text-text-primary">
                            {currentRuleData.categories.map(category => (
                                <li key={category.category_id} className="flex items-center justify-between py-1">
                                    {category.identifier}
                                    <Button variant="ghost" size="sm" onClick={() => handleUnlinkCategory(category.category_id)} disabled={linkingStatus.category}>
                                        <Trash2 size={16} className="text-red-500" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">No categories linked.</p>
                    )}
                </div>
            </div>

            <div className="flex justify-end mt-8">
                <Button variant="secondary" onClick={onClose}>Close Linkage Manager</Button>
            </div>
        </div>
    );
};

export default DiscountLinkageManager;
